/**
 * Adapted from PlayCanvas Engine's framework ResourceHandler / GSplatHandler at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type { Disposable } from '@/shared/types/core'

export type GSplatLoadSource = string | Blob

export interface GSplatLoadUrlObject {
  readonly load: GSplatLoadSource
  readonly original: string
}

export type GSplatLoadUrl = string | GSplatLoadUrlObject

export interface GSplatParserContext<TOptions = unknown> {
  readonly url: string
  readonly load: GSplatLoadSource
  readonly original: string
  readonly ext: string
  readonly basename: string
  readonly assetFileUrl: string
  readonly signal?: AbortSignal
  readonly options?: TOptions
  readonly handler: GSplatHandler<unknown, TOptions>
}

export interface IGSplatParser<TResult = unknown, TOptions = unknown> {
  handler?: GSplatHandler<TResult, TOptions> | null
  canParse(context: GSplatParserContext<TOptions>): boolean
  load(url: GSplatLoadUrlObject, context: GSplatParserContext<TOptions>): Promise<TResult>
}

export interface GSplatHandlerLoadOptions<TOptions = unknown> {
  readonly signal?: AbortSignal
  /** 资产注册表中真实 file URL；Octree 用它解析相对节点 URL。 */
  readonly assetFileUrl?: string
  readonly parserOptions?: TOptions
}

export class GSplatHandlerError extends Error {
  readonly code: 'GSPLAT_HANDLER_DISPOSED' | 'GSPLAT_PARSER_NOT_FOUND'
  readonly url: string

  constructor(
    code: 'GSPLAT_HANDLER_DISPOSED' | 'GSPLAT_PARSER_NOT_FOUND',
    url: string,
    message: string
  ) {
    super(message)
    this.name = 'GSplatHandlerError'
    this.code = code
    this.url = url
  }
}

/**
 * 后端无关的 3DGS parser registry。
 *
 * 和冻结 PlayCanvas 一致，parser 以 newest-first 顺序选择；load/original 分离使 blob URL
 * 仍能按原始文件名选中格式。未知格式在任何 fetch 发生前失败。
 */
export class GSplatHandler<TResult = unknown, TOptions = unknown> implements Disposable {
  readonly handlerType = 'gsplat'

  private readonly parserList: IGSplatParser<TResult, TOptions>[] = []
  private maxRetriesValue = 0
  private disposed = false

  get maxRetries(): number {
    return this.maxRetriesValue
  }

  set maxRetries(value: number) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error(`非法 GSplatHandler maxRetries: ${value}`)
    }
    this.maxRetriesValue = value
  }

  get parsers(): readonly IGSplatParser<TResult, TOptions>[] {
    return [...this.parserList]
  }

  get isDisposed(): boolean {
    return this.disposed
  }

  addParser(parser: IGSplatParser<TResult, TOptions>): Disposable {
    if (this.disposed) {
      throw new GSplatHandlerError(
        'GSPLAT_HANDLER_DISPOSED',
        '',
        'GSplatHandler 已销毁，不能注册 parser'
      )
    }
    if (!parser || typeof parser.canParse !== 'function' || typeof parser.load !== 'function') {
      throw new Error('GSplatHandler parser 必须实现 canParse/load')
    }
    parser.handler = this
    this.parserList.push(parser)

    let released = false
    return {
      dispose: () => {
        if (released) return
        released = true
        this.removeParser(parser)
      },
    }
  }

  removeParser(parser: IGSplatParser<TResult, TOptions>): void {
    const index = this.parserList.indexOf(parser)
    if (index === -1) return
    this.parserList.splice(index, 1)
    if (!this.parserList.includes(parser) && parser.handler === this) parser.handler = null
  }

  canLoad(url: GSplatLoadUrl): boolean {
    if (this.disposed) return false
    const normalized = normalizeGSplatLoadUrl(url)
    return this.selectParser(this.makeContext(normalized)) !== null
  }

  async load(
    url: GSplatLoadUrl,
    options: GSplatHandlerLoadOptions<TOptions> = {}
  ): Promise<TResult> {
    const normalized = normalizeGSplatLoadUrl(url)
    if (this.disposed) {
      throw new GSplatHandlerError(
        'GSPLAT_HANDLER_DISPOSED',
        normalized.original,
        `GSplatHandler 已销毁: ${normalized.original}`
      )
    }
    if (options.signal?.aborted) throw createAbortError(normalized.original)

    const context = this.makeContext(normalized, options)
    const parser = this.selectParser(context)
    if (!parser) {
      throw new GSplatHandlerError(
        'GSPLAT_PARSER_NOT_FOUND',
        normalized.original,
        `未找到 3DGS parser: ${normalized.original}`
      )
    }
    return parser.load(normalized, context)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    const parsers = this.parserList.splice(0)
    for (const parser of parsers) {
      if (parser.handler === this) parser.handler = null
    }
  }

  private makeContext(
    url: GSplatLoadUrlObject,
    options: GSplatHandlerLoadOptions<TOptions> = {}
  ): GSplatParserContext<TOptions> {
    const basename = getGSplatUrlBasename(url.original)
    const dot = basename.lastIndexOf('.')
    return {
      url: url.original,
      load: url.load,
      original: url.original,
      ext: dot === -1 ? '' : basename.slice(dot + 1),
      basename,
      assetFileUrl: options.assetFileUrl ?? url.original,
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.parserOptions !== undefined ? { options: options.parserOptions } : {}),
      handler: this as GSplatHandler<unknown, TOptions>,
    }
  }

  private selectParser(
    context: GSplatParserContext<TOptions>
  ): IGSplatParser<TResult, TOptions> | null {
    for (let index = this.parserList.length - 1; index >= 0; index--) {
      const parser = this.parserList[index]
      if (parser.canParse(context)) return parser
    }
    return null
  }
}

/**
 * 提取 GSplat 资源的逻辑路径。
 *
 * 除普通 HTTP/本地路径外，终端工程还会把 Windows 文件路径放在
 * `?path=<encoded-path>` 中，例如 `http://www.sharefe.com?path=C%3A%5C...`。
 * 格式识别和相对资源解析必须使用这个逻辑路径，而不能只看代理 URL 的 pathname。
 */
export function getGSplatUrlPath(url: string): string {
  const queryPath = readQueryPath(url)
  if (queryPath !== undefined) return safeDecodeUrlComponent(queryPath)

  const localFileMatch = url.match(/^https?:\/\/[^/]+\/local-file\/(.+)$/i)
  if (localFileMatch) return safeDecodeUrlComponent(localFileMatch[1])

  return safeDecodeUrlComponent(stripUrlQueryAndFragment(url))
}

/** 返回忽略大小写的逻辑 basename，兼容 Unix 与 Windows 分隔符。 */
export function getGSplatUrlBasename(url: string): string {
  const path = getGSplatUrlPath(url)
  const slash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'))
  return path.slice(slash + 1).toLowerCase()
}

/** 返回逻辑 basename 的小写扩展名（不含点号）。 */
export function getGSplatUrlExtension(url: string): string {
  const basename = getGSplatUrlBasename(url)
  const dot = basename.lastIndexOf('.')
  return dot === -1 ? '' : basename.slice(dot + 1)
}

export function normalizeGSplatLoadUrl(url: GSplatLoadUrl): GSplatLoadUrlObject {
  if (typeof url === 'string') {
    if (!url) throw new Error('GSplat load URL 不能为空')
    return { load: url, original: url }
  }
  if (!url || !url.original || !(typeof url.load === 'string' || url.load instanceof Blob)) {
    throw new Error('GSplat load/original URL 非法')
  }
  return url
}

function stripUrlQueryAndFragment(url: string): string {
  const query = url.indexOf('?')
  const fragment = url.indexOf('#')
  let end = url.length
  if (query >= 0) end = Math.min(end, query)
  if (fragment >= 0) end = Math.min(end, fragment)
  return url.slice(0, end)
}

function readQueryPath(url: string): string | undefined {
  const queryStart = url.indexOf('?')
  if (queryStart < 0) return undefined
  const fragmentStart = url.indexOf('#', queryStart + 1)
  const query = url.slice(queryStart + 1, fragmentStart < 0 ? url.length : fragmentStart)
  if (!query) return undefined

  try {
    const value = new URLSearchParams(query).get('path')
    if (value) return value
  } catch {
    // URLSearchParams 不可用或 query 非法时继续使用兼容正则。
  }

  const match = query.match(/(?:^|&)path=([^&]*)/i)
  return match?.[1] ? safeDecodeUrlComponent(match[1]) : undefined
}

function safeDecodeUrlComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function createAbortError(url: string): DOMException {
  return new DOMException(`GSplat load 已取消: ${url}`, 'AbortError')
}
